/**
 * Vault repair (docs/architecture/sync.md, "Vault repair"). The server's list
 * of this account's maps is the reference: a map is registered there before
 * the vault lists it and deleted there before it leaves the vault
 * (engine/crypto/vault-storage.js). Once per signed-in session, when the vault has
 * synced:
 *
 * 1. Proofs: an online map of this account that the vault lists and the
 *    server does not list with a valid owner proof (registered before keys
 *    and proofs came with the registration) is registered again, as opening
 *    it does: one request with its key, which fills a missing one, and its
 *    proof. A map the server no longer has is not created (`known`).
 * 2. Put back: a map owned on the server that the vault does not list
 *    (neither online nor on this device), with a valid proof and content,
 *    goes back into the vault at the top, titled from its content (decrypted
 *    here), with a short note. A map without a valid proof is never added:
 *    the server could have made it up. A map without content (its creation
 *    stopped before the vault listed it) has nothing to put back.
 */
import { ownerProofKey, isOwnerProofValid } from "../crypto/map-owner-proof.js";
import { mergeBackupState } from "../crypto/backup-state.js";
import { readMap } from "./map-format.js";
import { on, Events } from "../core/events.js";
import * as Y from "yjs";

const headers = { "X-Requested-With": "XMLHttpRequest" };
let deps = null;
let started = false;
let run = 0;
const stopRun = () => { run++; started = false; };
on(Events.AUTH_LOGOUT, stopRun);
on(Events.SESSION_ENDED, stopRun);

/**
 * What the repair does (pure).
 * @param {{owned: Array<{docId: string, proofValid: boolean}>,
 *   onlineIds: Set<string>, ownedOnlineIds: Set<string>, localIds: Set<string>}} state
 *   owned: the server's list (maps with a key); onlineIds: the vault's online
 *   maps, ownedOnlineIds: those this account owns
 * @returns {{prove: string[], restore: string[], unproven: string[]}}
 */
export function planRepair({ owned, onlineIds, ownedOnlineIds, localIds }) {
  const proven = new Set(owned.filter((doc) => doc.proofValid).map((doc) => doc.docId));
  const plan = { prove: [...ownedOnlineIds].filter((docId) => !proven.has(docId)), restore: [], unproven: [] };
  for (const doc of owned) {
    if (onlineIds.has(doc.docId) || localIds.has(doc.docId)) continue; // listed (on this device: being moved online)
    if (doc.proofValid) plan.restore.push(doc.docId);
    else plan.unproven.push(doc.docId);
  }
  return plan;
}

/**
 * Start the repair for this session, once the vault has synced (no-op after
 * the first run of the session).
 * @param {{getMasterKey: () => Uint8Array|null, vault: Object, userCRDT: Object,
 *   notify: (titles: string[]) => void}} callbacks
 */
export function scheduleVaultRepair(callbacks) {
  deps = callbacks;
  on(Events.USER_CRDT_STATE_CHANGED, ({ state }) => {
    if (state !== "synced" || started || !deps) return;
    started = true;
    const mine = ++run;
    void repairVault(deps, () => mine !== run).catch((err) => {
      console.warn("[VaultRepair] Stopped:", err);
    });
  });
}

async function getJson(path) {
  const response = await fetch(path, { credentials: "include", headers });
  if (!response.ok) throw new Error(`${path}: HTTP ${response.status}`);
  return response.json();
}

/** Register a map of the vault again, with its key and proof (as opening it does). */
async function register(vault, docId) {
  const response = await fetch("/api/docs", {
    method: "POST",
    credentials: "include",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ doc_id: docId, known: true, ...(await vault.registrationFields(docId)) }),
  });
  if (!response.ok) console.warn("[VaultRepair] Not registered again:", docId, response.status);
}

/**
 * A map's content on the server, decrypted here: its title (the top node's
 * name), or null when it has no content.
 */
async function contentTitle(vault, docId, wrappedDek) {
  const response = await fetch(`/api/docs/${encodeURIComponent(docId)}/state`, { credentials: "include" });
  if (!response.ok) return null; // deleted meanwhile, or not readable
  const stored = await response.json();
  if (!stored.baseline && !stored.deltas?.length) return null;
  const raw = await vault.unwrapAccountDEK(wrappedDek);
  try {
    const key = await crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["decrypt"]);
    const { state } = await mergeBackupState(null, stored, key, docId);
    if (!state) return null;
    const doc = new Y.Doc();
    try {
      Y.applyUpdate(doc, state);
      const { rootId, flat } = readMap(doc);
      return flat.get(rootId)?.name || "";
    } finally {
      doc.destroy();
    }
  } finally {
    raw.fill(0);
  }
}

/** One repair. Returns its plan (for logs and tests). */
export async function repairVault(cb, stopped = () => false) {
  const masterKey = cb.getMasterKey();
  if (!(masterKey instanceof Uint8Array)) return null;
  const key = await ownerProofKey(masterKey);
  const { docs = [] } = await getJson("/api/docs/owned");
  const owned = [];
  for (const doc of docs) {
    owned.push({ ...doc, proofValid: await isOwnerProofValid(key, doc.docId, doc.ownerProof) });
  }
  const online = cb.userCRDT.getAllOnlineDocEntries();
  const plan = planRepair({
    owned,
    onlineIds: new Set(online.map((entry) => entry.docId)),
    ownedOnlineIds: new Set(online.filter((entry) => !entry.role || entry.role === "owner").map((entry) => entry.docId)),
    localIds: new Set(cb.userCRDT.getAllLocalDocEntries().map((entry) => entry.docId)),
  });

  for (const docId of plan.prove) {
    if (stopped()) return plan;
    await register(cb.vault, docId);
  }
  if (plan.unproven.length) {
    console.warn(`[VaultRepair] ${plan.unproven.length} map(s) on the server not in the vault and without a valid proof: left as they are`, plan.unproven);
  }

  const byId = new Map(owned.map((doc) => [doc.docId, doc]));
  const top = cb.userCRDT.getAllFolders("online").find((folder) => folder.parentId === null)?.id || null;
  const titles = [];
  for (const docId of plan.restore) {
    if (stopped()) break;
    const { wrappedDek } = byId.get(docId);
    let title;
    try {
      title = await contentTitle(cb.vault, docId, wrappedDek);
    } catch (err) {
      console.warn("[VaultRepair] Content not read; not put back this time:", docId, err);
      continue;
    }
    if (title === null) continue; // no content: nothing to put back
    if (stopped() || cb.userCRDT.getDocStorage(docId)) continue; // back meanwhile (another device)
    const now = Date.now();
    cb.userCRDT.setDocEntry({ docId, title, folderId: top, role: "owner", wrappedDEK: wrappedDek, createdAt: now, updatedAt: now }, "online");
    titles.push(title);
    console.log("[VaultRepair] Put back a map missing from the vault:", docId);
  }
  if (titles.length) {
    await cb.vault.loadIndex();
    cb.notify(titles);
  }
  return plan;
}
